import { getOrganizationAssignablePlans } from "@common/constants/organization-billing";
import { FLAT_RATE_PLANS, flatRatePlanNames } from "@common/flatRateBilling";
import { ClientColumn, ClientView } from "@common/types/views/clientViews";
import { ViewType } from "@common/types/views/views";
import { ClientsView } from "@components/organizationView/clients/ClientsView";
import { TablePageHeader } from "@components/pageHeader/TablePageHeader";
import { useActiveOrganization } from "@hooks/useActiveOrganization";
import { useState, useCallback, useMemo } from "react";
import { Picker } from "@design-system/Picker/Picker.lib";
import { WorkspaceFilters } from "@common/schemas/client.schema";
import { WorkspaceSortValue } from "@common/types/views/workspaceViews";
import styles from "./PlanDetails.module.scss";

const clientView: ClientView = {
    type: ViewType.CLIENT,
    options: {
        selection: false,
        bulkActions: false,
    },
    columns: [
        { id: ClientColumn.NAME },
        { id: ClientColumn.PLAN },
        { id: ClientColumn.PRICE_MONTHLY },
        { id: ClientColumn.ACTIONS },
    ],
    filters: {},
};

interface Props {
    plan: FLAT_RATE_PLANS;
}

export const PlanDetails: React.FC<Props> = ({ plan }) => {
    const allowedPlans = useActiveOrganization()?.allowedPlans;
    const planFilterItems = useMemo(() => {
        const assignablePlans = getOrganizationAssignablePlans(allowedPlans);
        const planOptions = assignablePlans.some((option) => option === plan)
            ? assignablePlans
            : [...assignablePlans, plan];
        return planOptions.map((option) => ({
            value: option,
            label: flatRatePlanNames[option],
        }));
    }, [allowedPlans, plan]);
    const [page, setPage] = useState(1);
    const [currentSort, setCurrentSort] = useState<WorkspaceSortValue>(
        WorkspaceSortValue.NAME_ASC,
    );
    const [filters, setFilters] = useState<WorkspaceFilters>({
        search: "",
        plan,
    });

    const changeSort = useCallback(
        (sort: WorkspaceSortValue) => {
            setCurrentSort(sort);
            setPage(1);
        },
        [setCurrentSort, setPage],
    );

    const handleFiltersChange = useCallback((newFilters: WorkspaceFilters) => {
        setFilters((currentFilters) => ({
            ...currentFilters,
            ...newFilters,
        }));
        setPage(1); // Reset page when filters change
    }, []);

    return (
        <article className={styles.planDetails}>
            <TablePageHeader
                search={filters.search}
                onSearchChange={(val) =>
                    handleFiltersChange({ search: val ?? "" })
                }
                filters={filters}
                onFiltersChange={handleFiltersChange}
                isFullPage
            >
                <Picker.Select
                    items={planFilterItems}
                    value={filters.plan ?? null}
                    onValueChange={(newPlan) =>
                        handleFiltersChange({ plan: newPlan ?? undefined })
                    }
                    placeholder="All plans"
                    trigger={
                        <Picker.Button
                            variant="primary"
                            size="md"
                            className={styles.planSelect}
                        />
                    }
                />
            </TablePageHeader>
            <ClientsView
                filters={filters}
                view={clientView}
                page={page}
                sort={currentSort}
                onPageChange={setPage}
                onSortChange={changeSort}
            />
        </article>
    );
};
